import React, { useMemo, useState } from 'react';
import { RISK_LABEL, REVIEW_FILTER_OPTIONS } from './vendorLabels';

/**
 * Filter rail for the vendor register. A sibling of the directory's
 * DepartmentFilterSidebar: same `dir-side` classes, same narrow-screen
 * disclosure, same checkbox rows with counts. It's a separate component because
 * the department rail is a tree with rolled-up branch counts, and specialities
 * are a flat list next to two single-choice selects (the server takes one risk
 * and one review status).
 *
 * Counts come from the list response's `facets`. `counts` is null when that read
 * failed or hasn't arrived, and then no number is shown at all: a missing count
 * must never read as "0 vendors".
 */
const CHEVRON = (
  <svg className="dir-side__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="m9 6 6 6-6 6" />
  </svg>
);

const RISK_OPTIONS = Object.entries(RISK_LABEL);

function CountBadge({ count }) {
  if (count == null) return null;
  return (
    <span className="dir-side__count" title={`${count} ${count === 1 ? 'vendor' : 'vendors'}`}>
      {count}
    </span>
  );
}

function CheckRow({ checked, onChange, label, count, dir }) {
  return (
    <li>
      <div className={`dir-side__row vl-side__row ${checked ? 'is-checked' : ''}`}>
        <label className="dir-side__item">
          <input type="checkbox" checked={checked} onChange={onChange} />
          <span className="dir-side__name" dir={dir} title={label}>{label}</span>
          <CountBadge count={count} />
        </label>
      </div>
    </li>
  );
}

// Single choice as a select, "Any" first. Ten radio rows made the rail taller
// than the screen, so it needed its own scroll area and trapped the mouse wheel;
// two selects keep the whole rail in view and scrolling with the page.
function SelectGroup({ name, legend, value, options, anyLabel, onChange }) {
  const id = `${name}-title`;
  return (
    <div className="vl-side__group">
      <label className="dir-side__title vl-side__title" id={id} htmlFor={name}>{legend}</label>
      <select
        id={name}
        className={`vd-select vl-side__select${value ? ' is-set' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {[['', anyLabel], ...options].map(([key, label]) => (
          <option key={key || 'any'} value={key}>{label}</option>
        ))}
      </select>
    </div>
  );
}

export default function VendorFilterSidebar({
  specialities,
  specialitiesStatus,
  onRetrySpecialities,
  counts,
  filters,
  onChange,
  onToggleTag,
  onClearAll,
  activeCount,
}) {
  const [open, setOpen] = useState(false);
  const [find, setFind] = useState('');
  const { tagIds, risk, review, attention, archived } = filters;

  const shown = useMemo(() => {
    const needle = find.trim().toLowerCase();
    return needle ? specialities.filter((s) => s.name.toLowerCase().includes(needle)) : specialities;
  }, [specialities, find]);

  const bodyId = 'vl-side-body';
  return (
    <aside className="dir-side vl-side" aria-label="Filter vendors">
      <button
        type="button"
        className="dir-side__toggle"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((v) => !v)}
      >
        <span>
          Filter vendors
          {activeCount > 0 && <span className="dir-side__toggle-count"> · {activeCount} on</span>}
          {specialitiesStatus === 'error' && <span className="dir-side__toggle-error"> · specialities couldn&apos;t load</span>}
        </span>
        {CHEVRON}
      </button>

      <div id={bodyId} className={`dir-side__body${open ? ' is-open' : ''}`}>
        {activeCount > 0 && (
          <div className="vl-side__clearall">
            <button type="button" className="dir-side__clear" onClick={onClearAll}>Clear filters</button>
          </div>
        )}

        <section className="vl-side__group" aria-labelledby="vl-side-spec">
          <h2 className="dir-side__title vl-side__title" id="vl-side-spec">Specialities</h2>

          {specialities.length > 12 && (
            <input
              type="search"
              className="search-input dir-side__search"
              placeholder="Find a speciality…"
              value={find}
              onChange={(e) => setFind(e.target.value)}
              aria-label="Find a speciality"
            />
          )}

          {specialitiesStatus === 'loading' && <p className="dir-side__note" role="status">Loading specialities…</p>}
          {specialitiesStatus === 'error' && (
            <div className="dir-side__note dir-side__note--error" role="alert">
              <p>Couldn&apos;t load specialities.</p>
              <button type="button" className="btn btn-secondary btn-small" onClick={onRetrySpecialities}
                aria-label="Retry loading specialities">
                Try again
              </button>
            </div>
          )}
          {specialitiesStatus === 'ready' && specialities.length === 0 && (
            <p className="dir-side__note">No specialities set up yet.</p>
          )}
          {specialitiesStatus === 'ready' && specialities.length > 0 && (
            <>
              <ul className="dir-side__list vl-side__speclist">
                {shown.map((s) => (
                  <CheckRow
                    key={s.id}
                    checked={tagIds.includes(s.id)}
                    onChange={() => onToggleTag(s.id)}
                    label={s.name}
                    dir="auto"
                    // Absent from a successful facet read means no vendor matches: 0.
                    count={counts ? (counts.get(s.id) ?? 0) : null}
                  />
                ))}
              </ul>
              {shown.length === 0 && <p className="dir-side__note">No speciality matches that.</p>}
            </>
          )}
        </section>

        <SelectGroup
          name="vl-risk"
          legend="Risk"
          value={risk}
          options={RISK_OPTIONS}
          anyLabel="Any risk"
          onChange={(v) => onChange({ risk: v })}
        />
        <SelectGroup
          name="vl-review"
          legend="Review status"
          value={review}
          options={REVIEW_FILTER_OPTIONS}
          anyLabel="Any review status"
          onChange={(v) => onChange({ review: v })}
        />

        <div className="vl-side__group" role="group" aria-labelledby="vl-side-status">
          <h2 className="dir-side__title vl-side__title" id="vl-side-status">Status</h2>
          <ul className="dir-side__list">
            <CheckRow checked={attention} onChange={() => onChange({ attention: !attention })} label="Needs attention" />
            <CheckRow checked={archived} onChange={() => onChange({ archived: !archived })} label="Show archived" />
          </ul>
        </div>
      </div>
    </aside>
  );
}
